/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

/**
 * ============================================================================
 * Modules Hooks - React Query for Module Management
 * ============================================================================
 *
 * Custom hooks for module management data fetching and mutations.
 *
 * ============================================================================
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  modulesService,
  ModuleManagement,
  ModuleDependency,
  CreateDependencyRequest,
} from '@/services/superadmin/modules.service';
import { ApiError } from '@/services/api/types';

// Query keys for cache management
export const moduleKeys = {
  all: ['modules'] as const,
  list: () => [...moduleKeys.all, 'list'] as const,
  detail: (code: string) => [...moduleKeys.all, 'detail', code] as const,
  dependencies: () => [...moduleKeys.all, 'dependencies'] as const,
  moduleDeps: (code: string) => [...moduleKeys.all, 'deps', code] as const,
  dependents: (code: string) => [...moduleKeys.all, 'dependents', code] as const,
};

/**
 * Hook to fetch all modules with their management data.
 */
export function useModules() {
  return useQuery<ModuleManagement[], ApiError>({
    queryKey: moduleKeys.list(),
    queryFn: () => modulesService.getAllModules(),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Hook to fetch a single module's details.
 */
export function useModule(moduleCode: string) {
  return useQuery<ModuleManagement, ApiError>({
    queryKey: moduleKeys.detail(moduleCode),
    queryFn: () => modulesService.getModule(moduleCode),
    enabled: !!moduleCode,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Hook to fetch the full dependency graph.
 */
export function useDependencyGraph() {
  return useQuery<Record<string, ModuleDependency[]>, ApiError>({
    queryKey: moduleKeys.dependencies(),
    queryFn: () => modulesService.getDependencyGraph(),
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Hook to fetch dependencies for a specific module.
 */
export function useModuleDependencies(moduleCode: string) {
  return useQuery<ModuleDependency[], ApiError>({
    queryKey: moduleKeys.moduleDeps(moduleCode),
    queryFn: () => modulesService.getModuleDependencies(moduleCode),
    enabled: !!moduleCode,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Hook to fetch dependent modules.
 */
export function useDependentModules(moduleCode: string) {
  return useQuery<string[], ApiError>({
    queryKey: moduleKeys.dependents(moduleCode),
    queryFn: () => modulesService.getDependentModules(moduleCode),
    enabled: !!moduleCode,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Hook for creating a new dependency.
 */
export function useCreateDependency() {
  const queryClient = useQueryClient();

  return useMutation<ModuleDependency, ApiError, CreateDependencyRequest>({
    mutationFn: (request) => modulesService.createDependency(request),
    onSuccess: () => {
      // Invalidate all module-related queries
      queryClient.invalidateQueries({ queryKey: moduleKeys.all });
    },
  });
}

/**
 * Hook for deleting a dependency.
 */
export function useDeleteDependency() {
  const queryClient = useQueryClient();

  return useMutation<void, ApiError, { moduleCode: string; dependsOnCode: string }>({
    mutationFn: ({ moduleCode, dependsOnCode }) =>
      modulesService.deleteDependency(moduleCode, dependsOnCode),
    onSuccess: () => {
      // Invalidate all module-related queries
      queryClient.invalidateQueries({ queryKey: moduleKeys.all });
    },
  });
}
